import React from 'react'
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import CallMergeIcon from '@mui/icons-material/CallMerge'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { GetAccumulatedUtcTime, GetUtcTime } from '../../generic/DateTimeFunctions'
import { EXTERNAL_JOB_NODE_BACKGROUND_COLOR, EXTERNAL_JOB_NODE_TEXT_COLOR } from './nodes/TechnologyNode'
import { PROCESS_TREE_ROW_KIND, LANE_EVENT } from './processTree'
import { COVERAGE_STATE } from './processTreeCoverage'

/**
 * ProcessTreeRow.jsx
 * One row of the read-only process Tree surface (story 118). It PAINTS a row of the projection and
 * derives nothing: what to show is decided in `processTree.js`/`processTreeCoverage.js`.
 *
 * Three rules govern this file:
 *  - RN29.1/RN29.2 — a JOB row formats its time with `GetUtcTime` (so it never disagrees with the job
 *    properties panel, INV6); AGGREGATES (a set row) use `GetAccumulatedUtcTime`, which does not wrap at
 *    24 h (risk R5).
 *  - RN21/POL3 — skipped, external and FAIL reuse the language of the graph (opacity + line-through, the
 *    exported external colour pair, the red of the FAIL handle), never invented marks. The row background
 *    stays free: it belongs to the selection.
 *  - RN41/POL6 (amended by the PO on 2026-09-11) — the connector FOLDS and never selects; the row selects
 *    with a SINGLE click. A second click on a JOB row that is ALREADY selected opens it (`onOpen`): the
 *    operation editor. No `onDoubleClick` handler exists — a double click is simply the first click
 *    selecting and the second one opening, so a set or a branch still answers to nothing but selection.
 *  - PO review 2026-09-11 — a row marked `dependency` is one of the operations that must be finished
 *    before the selected one. It is painted, not re-ordered, and it never takes the selection.
 *
 * Memoized: the projection returns stable rows with stable keys, so folding one branch must not re-render
 * the other 300 (03-dominio.md §6, there is no virtualization in Jupiter).
 */

// Una sola densidad (decision del PO, 2026-09-09): la que antes era la comoda. El selector
// Compact/Comfortable no aportaba nada y se retiro.
const ROW_HEIGHT = 46
const FONT_SIZE = 15
const MATERIAL_ROW_HEIGHT_OFFSET = 8
// Explicito a proposito: antes se derivaba de la densidad compacta, que ya no existe.
// PO review 2026-09-21: 12.5 frente a los 15 de una operacion. A 14 la diferencia era de un pixel, que
// no es jerarquia: el material pesaba lo mismo que la fila que lo contiene. Bajarlo hace dos cosas a la
// vez — lo lee como subordinado y mete mas caracteres antes de que la elipsis tenga que cortar.
const MATERIAL_FONT_SIZE = 12.5
/** RN21: the red of the FAIL handle of the graph (`nodes/TechnologyNode.jsx:100`), on the connector and the branch label. */
const FAIL_BRANCH_COLOR = 'red'
const BRANCH_FAIL = 'FAIL'
const DASH = '—'

const COVERAGE_STATE_KEY = {
  [COVERAGE_STATE.NOT_ASSIGNED]: 'Not assigned',
  [COVERAGE_STATE.PARTIAL]: 'Partial',
  [COVERAGE_STATE.COMPLETE]: 'Complete',
  [COVERAGE_STATE.OVERASSIGNED]: 'Overassigned'
}

/**
 * PO review 2026-09-21: the pill wears the SAME colours the Resources section paints `remaining`
 * with (`WorkflowResource.jsx#getColorForRemaining`): green when nothing is left to assign, red
 * when too much is, yellow while something still is. There, yellow covers both "nothing assigned"
 * and "partly assigned" because `remaining > 0` cannot tell them apart; here the two states are
 * known, and they still share the yellow so the two surfaces agree at a glance — the WORD on the
 * pill is what tells them apart. Same tokens, not a second palette: a divergence here is a bug
 * report waiting to happen.
 */
const COVERAGE_STATE_COLOR = {
  [COVERAGE_STATE.NOT_ASSIGNED]: 'yellow.main',
  [COVERAGE_STATE.PARTIAL]: 'yellow.main',
  [COVERAGE_STATE.COMPLETE]: 'success.main',
  [COVERAGE_STATE.OVERASSIGNED]: 'error.main'
}

// PO review 2026-09-10: a branch header is selectable too — the inspector shows where it opens, where it
// closes and what it holds. Parallel and reference rows stay untouchable (S72).
// PO review 2026-09-21: MATERIAL joins them, reversing that part of S72. The row now truncates its name
// with an ellipsis, so the row alone no longer tells two components apart — clicking it is how the full
// name, code and position are read, in the side panel. Taking the truncation without the click would
// leave the end of a name unreachable, which is the very thing RN152 was written to prevent.
const SELECTABLE_KINDS = new Set([
  PROCESS_TREE_ROW_KIND.JOB,
  PROCESS_TREE_ROW_KIND.SET,
  PROCESS_TREE_ROW_KIND.BRANCH,
  PROCESS_TREE_ROW_KIND.MATERIAL
])

/**
 * RN25 (revisada 2026-09-08 con el PO, opcion A): la sangria la lleva el padding y el unico glifo que
 * queda es el chevron, solo donde hay algo que plegar. Los conectores tecleados se retiraron: al lado
 * del resto de MUI sonaban a volcado de terminal. El hueco del chevron se reserva aunque la fila no
 * pliegue, para que los codigos sigan cayendo en columna.
 */
// La sangria se topa: ASCOD_LETONIA encadena mas de 25 conjuntos, uno dentro de otro, y a 19 px por
// nivel el codigo se iba a 475 px bajando en diagonal hasta salirse. Pasado el tope, la profundidad
// deja de gastar ancho.
const INDENT_PX = 12
// El tope se queda, pero alto: con el escalon reservado para la contencion los niveles reales de un
// proceso son pocos. Sigue haciendo falta porque 25 conjuntos de verdad ANIDADOS siguen siendo posibles.
const MAX_INDENT_LEVELS = 12
const indentOf = (depth) => Math.min(depth, MAX_INDENT_LEVELS) * INDENT_PX

/**
 * PO review 2026-09-21, tercera pasada: el material tiene que leerse ABAJO A LA DERECHA de su job, sin
 * que haya que fijarse. Dos escalones de 12 px no bastaban — a tamano real son 24 px y el ojo los lee
 * como ruido de alineacion, no como pertenencia.
 *
 * Ahora el escalon extra son 28 px de hueco, y nada mas. Llego a llevar una guia vertical uniendolo con
 * su job; el PO la quito el mismo dia: el espacio ya dice de quien cuelga la fila y la linea solo sumaba
 * ruido a una pantalla que veniamos de aligerar.
 *
 * Va DESPUES del tope de sangria a proposito: pasado `MAX_INDENT_LEVELS` la profundidad deja de gastar
 * ancho, y ahi es justo donde el material acabaria alineado con su propio job.
 */
const MATERIAL_EXTRA_INDENT_PX = 28

/**
 * PO review 2026-09-21, ampliada el 22: lo que APARECE bajo una fila que se queda no sale de golpe, baja
 * desde ella. Vale para las lineas de material y su pie, y para las filas de un conjunto que se acaba de
 * abrir con la flecha. Siempre responde a una accion del usuario — encender Material, abrir el pie,
 * pulsar la flecha — que es cuando el movimiento explica algo: de donde ha salido lo que ves ahora.
 *
 * Lo que NO se anima es la primera pintada del proceso: ahi montan todas las filas a la vez y el arbol
 * entero se pondria a caer sin que nadie haya hecho nada. Por eso la vista dice QUE conjunto se acaba de
 * abrir en vez de animar cualquier fila que monte.
 *
 * El escalonado por indice las hace caer en cascada en vez de a bloque, con tope para que un conjunto de
 * treinta operaciones no tarde un segundo en acabar de entrar.
 * Se respeta `prefers-reduced-motion`: ahi aparecen sin mas, que es justo lo que pide quien lo activa.
 */
const ROW_DROP_MS = 180
const ROW_DROP_STAGGER_MS = 35
const ROW_DROP_MAX_STAGGER = 8

/** Ancho de la columna de caminos. Fija en todas las filas, para que los nombres caigan alineados. */
const BRANCH_COL_PX = 16

/**
 * La marca de camino, en UNA sola columna a la izquierda de los nombres. Dentro de un grupo paralelo
 * cada camino se abre con su marca y todo lo que va entre dos marcas pertenece al camino anterior,
 * asi que da igual que sean tres caminos o quince: no aparece ninguna diagonal.
 *
 * Se dibuja con bordes y no con los caracteres de caja, que se descuadran con la fuente y el zoom.
 * La forma es la misma: una vertical que baja, y un brazo hacia la derecha en cada camino.
 */
const BranchMark = ({ inParallel, branchHead, branchLast, branch, width = BRANCH_COL_PX }) => (
  <Box sx={{ width, flex: '0 0 ' + width + 'px', alignSelf: 'stretch', position: 'relative' }}>
    {inParallel
      ? (
        <>
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: branchLast ? '50%' : 0,
              borderLeft: '1px solid',
              borderColor: branch === BRANCH_FAIL ? FAIL_BRANCH_COLOR : 'divider'
            }}
          />
          {branchHead
            ? (
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  right: 0,
                  top: '50%',
                  borderTop: '1px solid',
                  borderColor: branch === BRANCH_FAIL ? FAIL_BRANCH_COLOR : 'divider'
                }}
              />
              )
            : null}
        </>
        )
      : null}
  </Box>
)
/** El area de pulsacion del chevron: 24 px dentro de una fila de 36, un objetivo comodo. */
const FOLD_HIT_PX = 24

/**
 * Story 167 (PO, 29/09/2026): the tree of the graph's panel is THIS row, compact, so the two trees look the same.
 * Compact changes only sizes and columns: the operation, what it lasts and a slot of its own on the right (the
 * SUB/OP tag of the panel). The technology and material columns do not fit a 272 px panel.
 */
// Fixed tracks for what lasts and for the tag: an auto track takes the width of each chip (OP is narrower than
// SUB) and the times stop falling in a column (PO, 29/09/2026).
// The gutter before the name is narrower too (PO: the rows started too far right): a thinner branch column, the
// split/merge icon only where there is one, and a smaller chevron.
const COMPACT = { rowHeight: 30, fontSize: 12.5, materialFontSize: 11, materialHeightOffset: 6, durationPx: 72, trailingPx: 38, sidePx: 6, branchColPx: 8, foldPx: 20 }
// In the panel the name and the code share a narrow line: they are cut, never wrapped over the row below.
const ONE_LINE_SX = { minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }

const ProcessTreeRowComponent = ({
  row,
  collapsed = false,
  selected = false,
  dependency = false,
  showDuration = true,
  alt = false,
  onSelect = () => {},
  onOpen = () => {},
  onJumpToNode = () => {},
  onToggle = () => {},
  onExpandMaterial = () => {},
  // La fila acaba de entrar porque se abrio el conjunto que la contiene, y en que orden cae.
  entering = false,
  enterIndex = 0,
  // Story 167: the panel's tree — compact sizes, its own test ids, its drop handlers and its trailing tag.
  compact = false,
  testIdPrefix = 'process-tree',
  rootProps = {},
  trailing = null,
  overlay = null,
  // Story 167 (PO, 29/09/2026): the set that holds this row is closing — the row rises and fades, the drop the other
  // way round, and in reverse order (`leaveIndex` 0 is the last row, the first to go).
  leaving = false,
  leaveIndex = 0
}) => {
  const { t_ } = useSmartTranslation()
  const isMaterialMore = row.kind === PROCESS_TREE_ROW_KIND.MATERIAL_MORE
  const isMaterial = row.kind === PROCESS_TREE_ROW_KIND.MATERIAL
  const isJob = row.kind === PROCESS_TREE_ROW_KIND.JOB
  const isSet = row.kind === PROCESS_TREE_ROW_KIND.SET
  const isParallel = row.kind === PROCESS_TREE_ROW_KIND.PARALLEL
  const isBranch = row.kind === PROCESS_TREE_ROW_KIND.BRANCH
  const isReference = row.kind === PROCESS_TREE_ROW_KIND.REFERENCE
  const isSelectable = SELECTABLE_KINDS.has(row.kind)
  const containsMatch = row.containsMatch === true && collapsed

  // El pie «+X materials» se maqueta como una linea de material: mismo alto, misma pista unica, misma
  // sangria y misma marca. Es una linea mas de la lista, no un control aparte pegado debajo.
  const isMaterialLike = isMaterial || isMaterialMore
  const rowHeight = compact ? COMPACT.rowHeight : ROW_HEIGHT
  const materialFontSize = compact ? COMPACT.materialFontSize : MATERIAL_FONT_SIZE
  const height = isMaterialLike ? rowHeight - (compact ? COMPACT.materialHeightOffset : MATERIAL_ROW_HEIGHT_OFFSET) : rowHeight
  const fontSize = isMaterialLike ? materialFontSize : (compact ? COMPACT.fontSize : FONT_SIZE)
  const materialIndent = compact ? MATERIAL_EXTRA_INDENT_PX / 2 : MATERIAL_EXTRA_INDENT_PX
  const gridColumns = () => {
    if (compact) {
      const trailing = COMPACT.trailingPx + 'px'
      if (isMaterialLike) {
        return 'minmax(0, 1fr) ' + trailing
      }
      return showDuration ? 'minmax(0, 1fr) ' + COMPACT.durationPx + 'px ' + trailing : 'minmax(0, 1fr) ' + trailing
    }
    if (isMaterialLike) {
      return 'minmax(0, 1fr)'
    }
    return showDuration
      ? 'minmax(0, 34rem) 118px 104px 148px minmax(0, 1fr)'
      : 'minmax(0, 34rem) 118px 148px minmax(0, 1fr)'
  }

  const skipTraceTitle = row.skipped
    ? [
        t_('Skipped'),
        row.skipTrace?.skippedBy ? t_('Skipped by') + ': ' + row.skipTrace.skippedBy : '',
        row.skipTrace?.skippedAt ? t_('Date') + ': ' + row.skipTrace.skippedAt : '',
        row.skipTrace?.skipReason ? t_('Reason') + ': ' + row.skipTrace.skipReason : ''
      ].filter(Boolean).join(' — ')
    : ''

  const renderTechnology = () => {
    if (isSet) {
      return t_('{{count}} operations', { count: row.jobCount })
    }
    if (row.kind !== PROCESS_TREE_ROW_KIND.JOB) {
      return ''
    }
    return row.technology || DASH
  }

  // RN29.1/RN29.2/RN30: job rows keep the panel's clock, aggregates never wrap at 24 h, no data is a dash.
  const renderDuration = () => {
    if (isParallel || isBranch || isReference || isMaterialLike) {
      return ''
    }
    if (row.timeIsUnknown) {
      return DASH
    }
    return isSet ? GetAccumulatedUtcTime(row.timeMs) : GetUtcTime(row.timeMs)
  }

  // RN32/RN50/RN59: only a job carries material; no coverage context loaded means NO DATA (a dash), not zero.
  const renderMaterial = () => {
    if (row.kind !== PROCESS_TREE_ROW_KIND.JOB) {
      return ''
    }
    if (!row.material) {
      return DASH
    }
    // RN126 (story #150): `hasVerdict` is read BEFORE `allComplete`. A job that consumes only raw
    // material has components to count and no BOM line to judge them by — saying "missing" about it
    // would be an accusation the data does not support. It gets the count alone.
    if (!row.material.hasVerdict) {
      return t_('{{count}} comp.', { count: row.material.componentCount })
    }
    return row.material.allComplete
      ? t_('{{count}} comp. · ok', { count: row.material.componentCount })
      : t_('{{count}} comp. · missing', { count: row.material.componentCount })
  }

  const renderOperationContent = () => {
    if (isParallel) {
      return <Typography component='span' sx={{ fontSize, fontStyle: 'italic' }}>{t_('Parallel · {{count}} branches', { count: row.branchCount })}</Typography>
    }
    if (isBranch) {
      return (
        <Typography component='span' sx={{ fontSize: fontSize - 1, fontStyle: 'italic', color: 'text.secondary', letterSpacing: '0.02em' }}>
          {t_('Branch {{index}} of {{count}}', { index: row.branchIndex, count: row.branchCount })}
        </Typography>
      )
    }
    // PO review 2026-09-21: el pie que abre el resto del material de este job. Boton de verdad, para que
    // el teclado llegue: la lista que despliega no tiene otra puerta.
    if (isMaterialMore) {
      return (
        <Box
          component='button'
          type='button'
          data-testid={testIdPrefix + '-material-more'}
          onClick={(event) => {
            event.stopPropagation()
            onExpandMaterial(row)
          }}
          // Esto NO es un boton: es una fila mas de la lista que dice cuantas quedan y se pulsa. Se le
          // puso forma de pastilla y el PO lo devolvio a texto el mismo dia — un boton aqui competiria
          // con las lineas de material entre las que vive (2026-09-22).
          sx={{
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            fontSize: materialFontSize,
            color: 'primary.main',
            cursor: 'pointer',
            textAlign: 'left',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          {row.hiddenMaterialCount > 0
            ? t_('+{{count}} materials', { count: row.hiddenMaterialCount })
            : t_('Show less material')}
        </Box>
      )
    }
    if (isMaterial) {
      const { code, name, position, quantity } = row.materialRow
      return (
        <Stack direction='row' spacing={1} alignItems='center' sx={{ fontSize: materialFontSize, minWidth: 0 }}>
          {/*
            * PO review 2026-09-21, reversing RN152: code and name are cut with an ellipsis instead of
            * wrapping. What the cut hides is read by clicking the row.
            *
            * RN200, la jerarquia de cesion — B4 del Judge (2026-09-23), MEDIDA en Chrome, no deducida:
            * con `0 1 auto` el codigo entraba en el reparto del deficit y un codigo de SEIS caracteres
            * (46 px) se recortaba a 32 px con el panel a 600, a 16 a 400 y a 5 a 260, mientras el
            * nombre aun ensenaba 182. O sea que la regla se incumplia en TODO el rango real del panel,
            * que va de 240 a 900.
            *
            * `0 0 auto` lo saca del reparto: cede el nombre y solo el nombre. El tope del 45% es lo que
            * impide que un codigo kilometrico —el maximo son 77 caracteres, 600 px— se coma la fila:
            * pasado ese ancho se corta el, que es justo lo que RN200 pide en segundo lugar.
            */}
          <Typography component='span' data-testid={testIdPrefix + '-material-code'} title={code} sx={{ fontSize: materialFontSize, fontWeight: 600, minWidth: 0, flex: '0 0 auto', maxWidth: '45%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{code}</Typography>
          <Typography component='span' title={name} sx={{ fontSize: materialFontSize, color: 'text.secondary', minWidth: 0, flex: '1 1 auto', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</Typography>
          {/* Cifras y pastilla no encogen: lo que cede es el nombre, que es lo unico que el panel
              lateral puede devolver entero. Un numero a medias no lo arregla ningun clic. */}
          <Typography component='span' sx={{ fontSize: materialFontSize, flexShrink: 0, whiteSpace: 'nowrap' }}>
            {quantity === null || quantity === undefined ? DASH : t_('{{quantity}} units', { quantity })}
          </Typography>
          {/* La referencia de posicion se pinta siempre que exista: la densidad que la ocultaba ya no esta. */}
          {position
            ? <Typography component='span' sx={{ fontSize: materialFontSize, color: 'text.secondary', flexShrink: 0, whiteSpace: 'nowrap' }}>{t_('pos. {{position}}', { position })}</Typography>
            : null}
          <Chip
            size='small'
            data-testid='process-tree-coverage-state'
            data-coverage-state={row.materialRow.state}
            label={t_(COVERAGE_STATE_KEY[row.materialRow.state])}
            sx={{
              height: materialFontSize + 4,
              fontSize: materialFontSize - 1,
              flexShrink: 0,
              backgroundColor: COVERAGE_STATE_COLOR[row.materialRow.state],
              // Los tres colores del veredicto son claros; el texto va en negro en los tres para
              // que se lea igual en cualquiera, como hace `yellow.contrastText` del tema.
              color: 'common.black'
            }}
          />
        </Stack>
      )
    }
    return (
      <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0 }}>
        {/* RN26: an empty name leaves this cell empty; the code NEVER stands in for it. */}
        <Typography component='span' data-testid={testIdPrefix + '-row-name'} sx={{ fontSize, fontWeight: 600, color: isSelectable && selected ? 'primary.main' : undefined, ...(compact ? ONE_LINE_SX : {}) }}>{row.name}</Typography>
        <Typography component='span' data-testid={testIdPrefix + '-row-code'} sx={{ fontSize: fontSize - 1, color: 'text.secondary', ...(compact ? ONE_LINE_SX : {}) }}>{row.code}</Typography>
        {isReference
          ? (
            <Typography
              component='span'
              data-testid='process-tree-reference-link'
              onClick={(event) => {
                event.stopPropagation()
                onJumpToNode(row.nodeId)
              }}
              sx={{
                fontSize: fontSize - 1,
                color: 'primary.main',
                fontStyle: 'italic',
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {row.referenceBranchIndex > 0
                ? t_('Already shown in branch {{index}}', { index: row.referenceBranchIndex })
                : t_('Already shown above')}
            </Typography>
            )
          : null}
        {row.branch === 'FAIL' ? <Chip size='small' data-testid='process-tree-chip-rework' label={t_('Rework')} sx={{ height: fontSize + 4, fontSize: fontSize - 1, color: FAIL_BRANCH_COLOR, borderColor: FAIL_BRANCH_COLOR }} variant='outlined' /> : null}
        {row.skipped
          ? (
            <Tooltip title={skipTraceTitle}>
              <span data-testid='process-tree-skip-tooltip' title={skipTraceTitle}>
                <Chip size='small' data-testid='process-tree-chip-skipped' label={t_('Skipped')} sx={{ height: fontSize + 4, fontSize: fontSize - 1 }} />
              </span>
            </Tooltip>
            )
          : null}
        {row.external
          ? (
            <Chip
              size='small'
              data-testid='process-tree-chip-extern'
              label={t_('Extern')}
              sx={{ height: fontSize + 4, fontSize: fontSize - 1, backgroundColor: EXTERNAL_JOB_NODE_BACKGROUND_COLOR, color: EXTERNAL_JOB_NODE_TEXT_COLOR }}
            />
            )
          : null}
        {row.dangling ? <Chip size='small' data-testid='process-tree-chip-dangling' label={t_('Unresolved link')} color='warning' variant='outlined' sx={{ height: fontSize + 4, fontSize: fontSize - 1 }} /> : null}
        {/* A warning glyph, the sentence in its tooltip: as a chip it took half the row (PO, 29/09/2026). */}
        {row.unreachable
          ? (
            <Tooltip title={t_('This operation is not connected to the process')} placement='top' disableInteractive>
              <WarningAmberRoundedIcon
                data-testid='process-tree-chip-unreachable'
                aria-label={t_('This operation is not connected to the process')}
                color='warning'
                sx={{ fontSize: fontSize + 3, flexShrink: 0 }}
              />
            </Tooltip>
            )
          : null}
        {row.failedHydration ? <Chip size='small' data-testid={testIdPrefix + '-chip-failed-set'} label={t_('This set could not be loaded')} color='warning' variant='outlined' sx={{ height: fontSize + 4, fontSize: fontSize - 1 }} /> : null}
        {containsMatch ? <Chip size='small' data-testid='process-tree-chip-contains-matches' label={t_('Contains matches')} color='info' variant='outlined' sx={{ height: fontSize + 4, fontSize: fontSize - 1 }} /> : null}
      </Stack>
    )
  }

  return (
    <Box
      {...rootProps}
      data-testid={testIdPrefix + '-row'}
      data-key={row.key}
      data-kind={row.kind}
      data-depth={row.depth}
      data-node-id={row.nodeId}
      aria-selected={isSelectable && selected}
      data-dependency={dependency}
      title={dependency ? t_('Required before the selected operation') : undefined}
      onClick={() => {
        if (!isSelectable) {
          return
        }
        // The second click on the selected operation opens it; the first one (and any click on a set
        // or a branch) only selects.
        // DILEMA 10 (PO, 2026-09-23): una linea de material ya marcada hace lo MISMO — el segundo clic
        // abre la ventana de su operacion, en la pestana Material. Un gesto, un resultado en toda la
        // superficie, y de paso es el atajo para ir a editarla.
        if ((isJob || isMaterial) && selected) {
          onOpen(row)
          return
        }
        onSelect(row)
      }}
      sx={{
        display: 'grid',
        // RN24: la columna de operacion NO se queda el ancho sobrante. Se lo lleva una pista final
        // vacia, asi las cifras viajan pegadas al nombre en vez de dejar 900 px de vacio en medio.
        // Story #150 (RN150/D1): a MATERIAL row spans one full-width track. It used to be laid on the
        // five tracks of an operation row, so the component name was boxed into the first 34rem while
        // the last track sat empty — the name wrapped inside that box and, with the row height fixed,
        // the wrapped text was painted over the row below it.
        gridTemplateColumns: gridColumns(),
        position: 'relative',
        // In the panel the row breathes on both sides: its tag never touches the edge, its outline never gets cut.
        ...(compact ? { pr: COMPACT.sidePx + 'px', pl: '2px' } : {}),
        alignItems: 'center',
        columnGap: 1,
        cursor: isSelectable ? 'pointer' : 'default',
        // RN24 (opcion A): zebra + hover verde con los tokens que el tema ya reserva para tablas;
        // la seleccion se marca ademas con un contorno verde entero, que sobrevive al hover (PO, 29/09/2026:
        // nada de filos de color a la izquierda). Una fila de la que depende la seleccionada se tiñe del mismo
        // verde, mas tenue, con el contorno mas claro: se lee como "va con la seleccion" sin confundirse con ella.
        // PO, 29/09/2026: the selected row is not lit, it is lighter — a surface a step above the zebra — and its name
        // turns to the green of MIC (`process-tree-row-name` below). What it depends on keeps a faint green tint.
        backgroundColor: isSelectable && selected
          ? (theme) => alpha(theme.palette.common.white, 0.07)
          : dependency
            ? (theme) => alpha(theme.palette.primary.main, 0.07)
            : (alt ? 'background.row2' : undefined),
        // Under the pointer the row lightens a little too — no green wash that would outshine the selection.
        '&:hover': isSelectable ? { backgroundColor: (theme) => alpha(theme.palette.common.white, 0.05) } : undefined,
        // In the panel the list scrolls: a row keeps its height however many there are, it never gets squeezed.
        ...(compact ? { flexShrink: 0 } : {}),
        // Al cerrar: la subida, la caida al reves — la fila sube y se desvanece, y despues sale de la lista.
        ...(leaving
          ? {
              pointerEvents: 'none',
              animation: ROW_DROP_MS + 'ms ease-in both process-tree-row-lift',
              animationDelay: Math.min(leaveIndex, ROW_DROP_MAX_STAGGER) * ROW_DROP_STAGGER_MS + 'ms',
              '@keyframes process-tree-row-lift': {
                from: { opacity: 1, transform: 'none' },
                to: { opacity: 0, transform: 'translateY(-8px)' }
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
            }
          : {}),
        // La caida: la fila entra desplazada hacia arriba y transparente, y baja hasta su sitio.
        ...(!leaving && (isMaterialLike || entering)
          ? {
              animation: ROW_DROP_MS + 'ms ease-out both process-tree-row-drop',
              animationDelay: Math.min(isMaterialLike ? (row.materialIndex || 0) : enterIndex, ROW_DROP_MAX_STAGGER) * ROW_DROP_STAGGER_MS + 'ms',
              '@keyframes process-tree-row-drop': {
                from: { opacity: 0, transform: 'translateY(-8px)' },
                to: { opacity: 1, transform: 'none' }
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
            }
          : {})
      }}
      /*
       * RN153, revertida (PO 2026-09-21, B1 del Judge 2026-09-23): la fila de material NO crece. Crecia
       * porque el nombre se partia en varias lineas; ahora se corta con elipsis (RN152 revertida) y
       * ocupa una sola, asi que su alto es fijo como el de cualquier otra fila. El `minHeight` que
       * quedaba era el mecanismo de la version muerta: sin nada que partirse no tenia de que crecer,
       * pero dejaba la puerta abierta a que un contenido inesperado descuadrara el ritmo del arbol.
       */
      // El orden de caida, visible en el DOM: es el unico rastro de la animacion que se puede comprobar
      // sin un navegador, porque el retardo vive en una clase generada y no en el estilo de la fila.
      data-enter-index={entering ? enterIndex : undefined}
      style={{ height: height + 'px', fontSize: fontSize + 'px' }}
    >
      <Stack direction='row' spacing={0} alignItems='stretch' data-testid='process-tree-cell-operation' sx={{ minWidth: 0 }} style={{ opacity: row.skipped ? 0.5 : 1, textDecoration: row.skipped ? 'line-through' : 'none' }}>
        <BranchMark inParallel={row.inParallel} branchHead={row.branchHead} branchLast={row.branchLast} branch={row.branch} width={compact ? COMPACT.branchColPx : BRANCH_COL_PX} />
        {/* Donde el proceso se parte y donde se vuelve a juntar: lo unico que se marca con icono. */}
        {!compact || row.laneEvent
          ? (
            <Box data-testid='process-tree-lane-event' sx={{ width: compact ? 14 : 18, flex: '0 0 ' + (compact ? 14 : 18) + 'px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main', '& svg': { fontSize: fontSize + (compact ? 2 : 4) } }}>
              {row.laneEvent === LANE_EVENT.SPLIT ? <CallSplitIcon fontSize='inherit' sx={{ transform: 'rotate(180deg)' }} /> : null}
              {row.laneEvent === LANE_EVENT.MERGE ? <CallMergeIcon fontSize='inherit' /> : null}
            </Box>
            )
          : null}
        <Stack direction='row' spacing={0.5} alignItems='center' data-testid='process-tree-indent' sx={{ minWidth: 0 }} style={{ paddingLeft: indentOf(row.depth) + 'px' }}>
          {/* PO review 2026-09-21: el escalon es hueco. Llevaba una guia vertical y sobraba — el espacio
              ya dice de quien cuelga la fila, y la linea solo anadia ruido a una pantalla que venimos
              de aligerar. Se queda el ancho, que es lo unico que hacia falta. */}
          {isMaterialLike
            ? <Box data-testid='process-tree-material-guide' sx={{ width: materialIndent, flex: '0 0 ' + materialIndent + 'px' }} />
            : null}
          {/* RN41/POL6: the connector folds and NEVER selects — hence the stopped propagation. */}
          <Box
            component='span'
            role='button'
            data-testid={testIdPrefix + '-connector'}
            onClick={(event) => {
              event.stopPropagation()
              if (row.collapsible) {
                onToggle(row)
              }
            }}
            tabIndex={row.collapsible ? 0 : -1}
            aria-expanded={row.collapsible ? !collapsed : undefined}
            data-collapsed={collapsed}
            onKeyDown={(event) => {
              if (row.collapsible && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault()
                event.stopPropagation()
                onToggle(row)
              }
            }}
            sx={{
              width: compact ? COMPACT.foldPx : FOLD_HIT_PX,
              height: compact ? COMPACT.foldPx : FOLD_HIT_PX,
              flex: '0 0 ' + (compact ? COMPACT.foldPx : FOLD_HIT_PX) + 'px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              userSelect: 'none',
              color: 'primary.main',
              cursor: row.collapsible ? 'pointer' : 'default',
              // RN155: the mark of a material row rides the gap the chevron already reserves, so it
              // costs no width. It is a label, not a control (RN156).
              visibility: (row.collapsible || isMaterialLike) ? 'visible' : 'hidden',
              pointerEvents: isMaterialLike ? 'none' : undefined,
              // el chevron tiene que pedir que lo pulses: area de 24 px, fondo al pasar por encima
              // y giro en vez de cambio de glifo, que es como se lee un desplegable en el resto de MUI.
              '&:hover': { backgroundColor: 'action.hover' },
              '&:active': { backgroundColor: 'action.selected' },
              '& svg': {
                fontSize: fontSize + 6,
                transition: 'transform 120ms ease',
                transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)'
              },
              '@media (prefers-reduced-motion: reduce)': { '& svg': { transition: 'none' } }
            }}
            style={{ color: row.branch === 'FAIL' ? FAIL_BRANCH_COLOR : undefined }}
          >
            {isMaterialLike ? <CategoryRoundedIcon data-testid='process-tree-material-mark' fontSize='inherit' /> : <KeyboardArrowRightIcon fontSize='inherit' />}
          </Box>
          {renderOperationContent()}
        </Stack>
      </Stack>
      {/* A material row has no technology, no duration and no material summary of its own: mounting the
          three empty cells is what left the width they were taking (RN150). */}
      {!isMaterialLike && !compact ? <Box data-testid='process-tree-cell-technology' sx={{ fontSize, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis' }}>{renderTechnology()}</Box> : null}
      {!isMaterialLike && showDuration ? <Box data-testid={testIdPrefix + '-cell-duration'} sx={{ fontSize, fontFamily: 'monospace', textAlign: 'right' }}>{renderDuration()}</Box> : null}
      {!isMaterialLike && !compact ? <Box data-testid='process-tree-cell-material' sx={{ fontSize, color: 'text.secondary', textAlign: 'right' }}>{renderMaterial()}</Box> : null}
      {compact ? <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>{trailing}</Box> : null}
      {overlay}
    </Box>
  )
}

const ProcessTreeRow = React.memo(ProcessTreeRowComponent)

export default ProcessTreeRow
export { COMPACT as COMPACT_ROW, ROW_DROP_MS, ROW_DROP_STAGGER_MS, ROW_DROP_MAX_STAGGER, BranchMark, COVERAGE_STATE_KEY, COVERAGE_STATE_COLOR, INDENT_PX, MATERIAL_EXTRA_INDENT_PX, MAX_INDENT_LEVELS, FOLD_HIT_PX, BRANCH_COL_PX, indentOf }
